import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const stirFryRecipes: RecipeFamily[] = [
  {
    id: "tofu-broccoli-stir-fry",
    nameTemplate: "Ginger {protein.name} & {vegetable.name} stir-fry",
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3, undefined, {
          tr: { preparation: "suyu sıkılmış ve küp doğranmış" },
        }),
        option("edamame", 280, "g", "shelled", 2, undefined, {
          tr: { preparation: "kabuğu soyulmuş" },
        }),
        option("chickpeas", 240, "g", "drained and dried", 1, undefined, {
          tr: { preparation: "süzülmüş ve kurutulmuş" },
        }),
      ]),
      slot("vegetable", "Vegetable", true, "broccoli", [
        option("broccoli", 350, "g", "cut into small florets", 3, undefined, {
          tr: { preparation: "küçük parçalara ayrılmış" },
        }),
        option(
          "cauliflower",
          350,
          "g",
          "cut into small florets",
          2,
          undefined,
          { tr: { preparation: "küçük parçalara ayrılmış" } },
        ),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3, undefined, {
          tr: { preparation: "pişmiş" },
        }),
        option("quinoa", 300, "g", "cooked", 1, undefined, {
          tr: { preparation: "pişmiş" },
        }),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated", 1, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped", 1, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch", undefined, 3),
        option("scallion", 2, "piece", "sliced", 2, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
        option("cashews", 2, "tbsp", "roughly chopped", 2, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("pumpkin-seeds", 2, "tbsp", undefined, 1),
      ]),
      pantry.water(45),
    ],
    steps: [
      {
        id: "mix-sauce",
        template:
          "Mix {sauce.name} with {water.name}, {ginger.name}, and {garlic.name}.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {water.name}, {ginger.name} ve {garlic.name} karıştırın.",
          },
        },
      },
      {
        id: "brown-protein",
        template:
          "Heat {oil.name} in a wide pan over high heat. Cook {protein.name} for 5 minutes, turning until browned.",
        translations: {
          tr: {
            template:
              "{oil.name} geniş bir tavada yüksek ateşte ısıtın. {protein.name} altın rengi olana kadar arada çevirerek 5 dakika pişirin.",
          },
        },
      },
      {
        id: "cook-vegetable",
        template:
          "Add {vegetable.name} and stir-fry for 4 to 5 minutes. Pour in the sauce and toss until glossy and tender-crisp.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ekleyin ve 4-5 dakika kavurun. Sosu ekleyip parlak ve diri kalana kadar karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template: "Serve over {base.name} and add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin, kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "aroma", "crunch"],
    editorialPriority: 10,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Zencefilli {protein.name} & {vegetable.name} kavurma",
      },
    },
  },
  {
    id: "chickpea-green-bean-stir-fry",
    nameTemplate: "Miso {protein.name} & {vegetable.name} stir-fry",
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 22,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3, undefined, {
          tr: { preparation: "süzülmüş ve kurutulmuş" },
        }),
        option("tofu", 300, "g", "pressed and cubed", 2, undefined, {
          tr: { preparation: "suyu sıkılmış ve küp doğranmış" },
        }),
      ]),
      slot("vegetable", "Green vegetable", true, "green-beans", [
        option("green-beans", 300, "g", "trimmed and halved", 3, undefined, {
          tr: { preparation: "ayıklanmış ve ikiye bölünmüş" },
        }),
        option("broccoli", 300, "g", "cut into small florets", 2, undefined, {
          tr: { preparation: "küçük parçalara ayrılmış" },
        }),
      ]),
      slot("pepper", "Sweet vegetable", true, "bell-pepper", [
        option("bell-pepper", 250, "g", "cut into strips", 3, undefined, {
          tr: { preparation: "şerit şeklinde doğranmış" },
        }),
        option("carrot", 250, "g", "cut into thin matchsticks", 1, undefined, {
          tr: { preparation: "ince çubuklar halinde doğranmış" },
        }),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 1, undefined, {
          tr: { preparation: "pişmiş" },
        }),
      ]),
      slot("sauce", "Umami sauce", true, "miso", [
        option("miso", 1, "tbsp", undefined, 3),
        option("soy-sauce", 2, "tbsp", undefined, 2),
      ]),
      slot("ginger", "Aromatic", true, "ginger", [
        option("ginger", 20, "g", "finely grated", 3, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
        option("garlic", 2, "piece", "finely chopped", 2, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("lime", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.water(45),
    ],
    steps: [
      {
        id: "mix-sauce",
        template:
          "Whisk {sauce.name}, {water.name}, and {ginger.name} into a loose sauce.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {water.name} ve {ginger.name} çırparak akıcı bir sos hazırlayın.",
          },
        },
      },
      {
        id: "cook-vegetables",
        template:
          "Heat {oil.name} in a wide pan. Stir-fry {vegetable.name} and {pepper.name} for 5 minutes.",
        translations: {
          tr: {
            template:
              "{oil.name} geniş bir tavada ısıtın. {vegetable.name} ve {pepper.name} 5 dakika kavurun.",
          },
        },
      },
      {
        id: "glaze",
        template:
          "Add {protein.name} and the sauce. Toss for 3 to 4 minutes, until hot and evenly glazed.",
        translations: {
          tr: {
            template:
              "{protein.name} ve sosu ekleyin. Sıcak ve sosla iyice kaplanana kadar 3-4 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template: "Add {finish.name} if using and serve with {base.name}.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyin ve {base.name} ile servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "fresh", "crunch"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: { nameTemplate: "Misolu {protein.name} & {vegetable.name} kavurma" },
    },
  },
  {
    id: "egg-cabbage-noodle-stir-fry",
    nameTemplate: "{protein.name}, {vegetable.name} & noodle stir-fry",
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "egg", [
        option(
          "egg",
          3,
          "piece",
          "beaten",
          3,
          {
            safetyCue: "Stir the beaten eggs until no liquid egg remains",
          },
          {
            tr: {
              preparation: "çırpılmış",
              instructionValues: {
                safetyCue:
                  "Çırpılmış yumurtaları sıvı kalmayana kadar karıştırın",
              },
            },
          },
        ),
        option(
          "tofu",
          280,
          "g",
          "pressed and crumbled",
          2,
          {
            safetyCue:
              "Cook the tofu, breaking it into crumbles, until lightly browned",
          },
          {
            tr: {
              preparation: "suyu sıkılmış ve ufalanmış",
              instructionValues: {
                safetyCue: "Tofuyu ufalayarak hafif kızarana kadar pişirin",
              },
            },
          },
        ),
      ]),
      slot("vegetable", "Main vegetable", true, "cabbage", [
        option("cabbage", 300, "g", "thinly sliced", 3, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
        option("spinach", 220, "g", "roughly chopped", 1, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
      ]),
      slot("carrot", "Crunchy vegetable", true, "carrot", [
        option("carrot", 200, "g", "cut into thin matchsticks", 3, undefined, {
          tr: { preparation: "ince çubuklar halinde doğranmış" },
        }),
        option("bell-pepper", 200, "g", "cut into thin strips", 2, undefined, {
          tr: { preparation: "ince şeritler halinde doğranmış" },
        }),
      ]),
      slot("base", "Noodles", true, "noodles", [
        option("noodles", 300, "g", "cooked and drained", 1, undefined, {
          tr: { preparation: "pişirilmiş ve süzülmüş" },
        }),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("ginger", "Aromatic", true, "ginger", [
        option("ginger", 15, "g", "finely grated", 3, undefined, {
          tr: { preparation: "ince rendelenmiş" },
        }),
        option("garlic", 2, "piece", "finely chopped", 2, undefined, {
          tr: { preparation: "ince doğranmış" },
        }),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
    ],
    steps: [
      {
        id: "cook-protein",
        template:
          "Heat half the {oil.name} in a large pan. {protein.safetyCue}, then move it to a plate.",
        translations: {
          tr: {
            template:
              "{oil.name} yarısını büyük bir tavada ısıtın. {protein.safetyCue}, ardından bir tabağa alın.",
          },
        },
      },
      {
        id: "cook-vegetables",
        template:
          "Add the remaining {oil.name}, then stir-fry {vegetable.name}, {carrot.name}, and {ginger.name} for 4 minutes.",
        translations: {
          tr: {
            template:
              "Kalan {oil.name} ekleyin, ardından {vegetable.name}, {carrot.name} ve {ginger.name} 4 dakika kavurun.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name}, {protein.name}, and {sauce.name}. Toss over high heat until everything is hot and evenly coated.",
        translations: {
          tr: {
            template:
              "{base.name}, {protein.name} ve {sauce.name} ekleyin. Yüksek ateşte her şey sıcak ve eşit kaplanana kadar karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template: "Add {finish.name} if using and serve immediately.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ekleyin ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "fresh", "heat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name}, {vegetable.name} ve erişte kavurması",
      },
    },
  },
  {
    id: "tomato-onion-stir-fry",
    nameTemplate:
      "{vegetable.name} & {onion.name} stir-fry with {protein.name}",
    translations: {
      tr: {
        nameTemplate:
          "{protein.name} ile {vegetable.name} & {onion.name} kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 14,
    totalMinutes: 20,
    slots: [
      slot("vegetable", "Tomato", true, "tomato", [
        option("tomato", 300, "g", "cut into wedges"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "cut into thin wedges"),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3),
        option("tofu", 300, "g", "pressed and cubed", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("noodles", 300, "g", "cooked and drained", 2),
      ]),
      slot("ginger", "Ginger", false, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("finish", "Finish", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch", undefined, 3),
        option("pumpkin-seeds", 2, "tbsp", undefined, 1),
      ]),
    ],
    steps: [
      {
        id: "start",
        template:
          "Heat {oil.name} in a wok or large pan over high heat. Add {onion.name} and {garlic.name}; stir-fry for 2 minutes until lightly charred at the edges.",
        translations: {
          tr: {
            template:
              "Wok veya büyük bir tavada {oil.name} yüksek ateşte ısıtın. {onion.name} ve {garlic.name} ekleyin; kenarları hafifçe kararana kadar 2 dakika kavurun.",
          },
        },
      },
      {
        id: "add-protein",
        template: "Add {protein.name} and stir-fry for 3 minutes until golden.",
        translations: {
          tr: {
            template:
              "{protein.name} ekleyin ve altın rengi olana kadar 3 dakika kavurun.",
          },
        },
      },
      {
        id: "add-tomato",
        template:
          "Add {vegetable.name} and {ginger.name} if using; stir-fry for 2 minutes. Pour over {sauce.name} and toss for 1 minute until everything is coated.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve kullanıyorsanız {ginger.name} ekleyin; 2 dakika kavurun. {sauce.name} dökün ve her şey kaplanana kadar 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template: "Serve over {base.name} and scatter {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin ve kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "acid", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-tomato-onion-fajita-stir-fry",
    nameTemplate:
      "Smoky {protein.name}, {vegetable.name} & {onion.name} stir-fry",
    translations: {
      tr: {
        nameTemplate:
          "Dumanlı {protein.name}, {vegetable.name} & {onion.name} kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 22,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("black-beans", 240, "g", "drained", 1),
      ]),
      slot("vegetable", "Tomato", true, "tomato", [
        option("tomato", 300, "g", "cut into wedges"),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "cut into thin wedges"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Smoky spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("finish", "Bright finish", false, "lime", [
        option("lime", 1, "piece", "cut into wedges", 3),
        option("cilantro", 15, "g", "chopped", 2),
        option("avocado", 1, "piece", "sliced", 1),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "start",
        template:
          "Heat {oil.name} in a wide pan over high heat. Add {onion.name} and {garlic.name}; stir-fry for 2 minutes until starting to char.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} yüksek ateşte ısıtın. {onion.name} ve {garlic.name} ekleyin; hafifçe kararana kadar 2 dakika kavurun.",
          },
        },
      },
      {
        id: "add-protein",
        template:
          "Add {protein.name} and {spice.name}; stir-fry for 4 minutes until the protein is browned and fragrant.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {spice.name} ekleyin; protein kızarıp güzel koku verinceye kadar 4 dakika kavurun.",
          },
        },
      },
      {
        id: "add-tomato",
        template:
          "Add {vegetable.name} and {salt.name}; stir-fry for 2 minutes until just softened but still holding its shape.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve {salt.name} ekleyin; şeklini korurken hafifçe yumuşayana kadar 2 dakika kavurun.",
          },
        },
      },
      {
        id: "serve",
        template: "Serve over {base.name} and add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin ve kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "acid", "aroma"],
    editorialPriority: 7,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "mushroom-spinach-stir-fry",
    nameTemplate: "Gingery {vegetable.name} & {greens.name} stir-fry",
    translations: {
      tr: {
        nameTemplate: "Zencefilli {vegetable.name} & {greens.name} kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("vegetable", "Mushroom", true, "mushroom", [
        option("mushroom", 350, "g", "sliced", 3),
        option("eggplant", 350, "g", "cut into bite-sized pieces", 1),
      ]),
      slot("greens", "Leafy greens", true, "spinach", [
        option("spinach", 200, "g", "roughly chopped", 3),
        option("cabbage", 250, "g", "thinly sliced", 1),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3),
        option("edamame", 200, "g", "shelled", 2),
        option("chickpeas", 240, "g", "drained and dried", 1),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("noodles", 300, "g", "cooked and drained", 2),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
        option("scallion", 2, "piece", "sliced", 1),
      ]),
      pantry.water(45),
    ],
    steps: [
      {
        id: "mix-sauce",
        template:
          "Stir {sauce.name} with {water.name}, {garlic.name}, and {ginger.name}.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {water.name}, {garlic.name} ve {ginger.name} karıştırın.",
          },
        },
      },
      {
        id: "cook-protein",
        template:
          "Heat {oil.name} in a wide pan over high heat. Cook {protein.name} for 4 minutes until golden; remove to a plate.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} yüksek ateşte ısıtın. {protein.name} altın rengi olana kadar 4 dakika pişirin; bir tabağa alın.",
          },
        },
      },
      {
        id: "cook-vegetable",
        template:
          "In the same pan, stir-fry {vegetable.name} for 3 minutes. Add {greens.name} and toss for 1 minute until just wilted.",
        translations: {
          tr: {
            template:
              "Aynı tavada {vegetable.name} 3 dakika kavurun. {greens.name} ekleyin ve hafifçe solana kadar 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "glaze",
        template:
          "Return {protein.name} to the pan, pour over the sauce, and toss for 1 minute until glossy and coated.",
        translations: {
          tr: {
            template:
              "{protein.name} tavaya geri ekleyin, sosu dökün ve parlak şekilde kaplanana kadar 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template: "Serve over {base.name} and add {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin ve kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "aroma", "crunch"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "peanut-tofu-noodle-stir-fry",
    nameTemplate: "Peanut {protein.name} & {vegetable.name} noodles",
    translations: {
      tr: {
        nameTemplate: "Fıstıklı {protein.name} & {vegetable.name} erişte",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("edamame", 200, "g", "shelled", 2),
        option("chickpeas", 240, "g", "drained and dried", 1),
      ]),
      slot("vegetable", "Vegetable", true, "carrot", [
        option("carrot", 200, "g", "cut into thin matchsticks", 3),
        option("cabbage", 250, "g", "thinly sliced", 2),
        option("bell-pepper", 200, "g", "cut into thin strips", 1),
      ]),
      slot("base", "Noodles", true, "noodles", [
        option("noodles", 300, "g", "cooked and drained"),
      ]),
      slot("sauce", "Sauce base", true, "peanut-butter", [
        option("peanut-butter", 2, "tbsp"),
        option("tahini", 2, "tbsp"),
      ]),
      slot("season", "Seasoning", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp", undefined, 3),
        option("miso", 1, "tsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lime", [
        option("lime", 1, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Fresh finish", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("scallion", 2, "piece", "sliced", 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.water(30),
    ],
    steps: [
      {
        id: "make-sauce",
        template:
          "Whisk {sauce.name}, {season.name}, {acid.name} juice, {ginger.name}, {garlic.name}, and {water.name} into a smooth sauce.",
        translations: {
          tr: {
            template:
              "{sauce.name}, {season.name}, {acid.name} suyu, {ginger.name}, {garlic.name} ve {water.name} çırparak pürüzsüz bir sos hazırlayın.",
          },
        },
      },
      {
        id: "cook-protein",
        template:
          "Heat {oil.name} over high heat. Cook {protein.name} for 4 to 5 minutes until golden.",
        translations: {
          tr: {
            template:
              "{oil.name} yüksek ateşte ısıtın. {protein.name} altın rengi olana kadar 4-5 dakika pişirin.",
          },
        },
      },
      {
        id: "add-vegetable",
        template: "Add {vegetable.name} and stir-fry for 2 minutes.",
        translations: {
          tr: {
            template: "{vegetable.name} ekleyin ve 2 dakika kavurun.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name} and the sauce. Toss over high heat for 2 minutes until everything is coated.",
        translations: {
          tr: {
            template:
              "{base.name} ve sosu ekleyin. Yüksek ateşte her şey kaplanana kadar 2 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template: "Top with {finish.name} if using and serve immediately.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} ile üstünü kapatın ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "acid", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "eggplant-black-bean-stir-fry",
    nameTemplate: "Garlicky {vegetable.name} & {protein.name} stir-fry",
    translations: {
      tr: {
        nameTemplate: "Sarımsaklı {vegetable.name} & {protein.name} kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 17,
    totalMinutes: 22,
    slots: [
      slot("vegetable", "Vegetable", true, "eggplant", [
        option("eggplant", 400, "g", "cut into bite-sized pieces", 3),
        option("zucchini", 400, "g", "cut into thick half-moons", 1),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("chickpeas", 240, "g", "drained and dried", 2),
        option("tofu", 280, "g", "pressed and cubed", 1),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("noodles", 300, "g", "cooked and drained", 1),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "basil", [
        option("basil", 15, "g", "torn", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      slot("finish", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
      pantry.water(30),
    ],
    steps: [
      {
        id: "cook-vegetable",
        template:
          "Heat {oil.name} in a wide pan over high heat. Cook {vegetable.name} for 6 to 7 minutes, stirring occasionally, until golden and tender.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada {oil.name} yüksek ateşte ısıtın. {vegetable.name} ara sıra karıştırarak altın rengi ve yumuşayana kadar 6-7 dakika pişirin.",
          },
        },
      },
      {
        id: "add-garlic",
        template: "Add {garlic.name} and stir for 30 seconds until fragrant.",
        translations: {
          tr: {
            template:
              "{garlic.name} ekleyin ve güzel koku verinceye kadar 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "glaze",
        template:
          "Add {protein.name}, {sauce.name}, and {water.name}. Toss over high heat for 2 minutes until the protein is hot and coated.",
        translations: {
          tr: {
            template:
              "{protein.name}, {sauce.name} ve {water.name} ekleyin. Protein ısınıp sosla kaplanana kadar yüksek ateşte 2 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Serve over {base.name} scattered with {herb.name} and {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin, kullanıyorsanız {herb.name} ve {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "starch", "umami", "aroma", "heat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "miso-glazed-eggplant-stir-fry",
    nameTemplate: "Miso-glazed {vegetable.name} stir-fry",
    translations: {
      tr: {
        nameTemplate: "Misolu sırlı {vegetable.name} kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("vegetable", "Main vegetable", true, "eggplant", [
        option("eggplant", 400, "g", "cut into 2 cm cubes", 3),
        option("mushroom", 350, "g", "thickly sliced", 2),
      ]),
      slot("sauce", "Miso glaze", true, "miso", [option("miso", 2, "tbsp")]),
      slot("umami", "Umami depth", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp"),
      ]),
      slot("aroma", "Aromatic", true, "ginger", [
        option("ginger", 20, "g", "finely grated"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 0.5, "tsp"),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("quinoa", 300, "g", "cooked", 2),
      ]),
      slot("finish", "Garnish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("scallion", 2, "piece", "thinly sliced", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.water(3),
      pantry.salt,
    ],
    steps: [
      {
        id: "mix-glaze",
        template:
          "Stir together {sauce.name}, {umami.name}, and {water.name} in a small bowl until smooth. Set aside.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {sauce.name}, {umami.name} ve {water.name} pürüzsüz olana kadar karıştırın. Bir kenara koyun.",
          },
        },
      },
      {
        id: "cook-vegetable",
        template:
          "Heat {oil.name} in a large frying pan or wok over high heat. Add {vegetable.name} in a single layer and cook without stirring for 3 minutes until browned. Stir and cook 2 minutes more. Season lightly with {salt.name}.",
        translations: {
          tr: {
            template:
              "Büyük bir tavada veya wokta {oil.name} yüksek ateşte ısıtın. {vegetable.name} tek katman olarak ekleyin ve karıştırmadan 3 dakika kızarana kadar pişirin. Karıştırın ve 2 dakika daha pişirin. {salt.name} ile hafifçe tatlandırın.",
          },
        },
      },
      {
        id: "add-aromatics",
        template:
          "Push the {vegetable.name} to one side. Add {garlic.name}, {aroma.name}, and {heat.name} if using to the empty side; stir for 30 seconds.",
        translations: {
          tr: {
            template:
              "{vegetable.name} bir kenara itin. Boş tarafa {garlic.name}, {aroma.name} ve kullanıyorsanız {heat.name} ekleyin; 30 saniye karıştırın.",
          },
        },
      },
      {
        id: "glaze",
        template:
          "Pour the miso glaze over everything and toss over high heat for 1 minute until the {vegetable.name} is coated and caramelised.",
        translations: {
          tr: {
            template:
              "Miso sırını her şeyin üzerine dökün ve {vegetable.name} karamelleşip kaplanana kadar yüksek ateşte 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Serve over {base.name}, scattered with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin, kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "fat", "aroma", "starch"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "bok-choy-tempeh-stir-fry",
    nameTemplate: "{protein.name} & {vegetable.name} stir-fry",
    translations: {
      tr: {
        nameTemplate: "{protein.name} & {vegetable.name} kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "tempeh", [
        option("tempeh", 250, "g", "sliced into strips", 3),
        option("tofu", 300, "g", "pressed and cubed", 2),
        option("edamame", 280, "g", "shelled", 1),
      ]),
      slot("vegetable", "Vegetable", true, "bok-choy", [
        option("bok-choy", 400, "g", "halved or quartered", 3),
        option("broccoli", 350, "g", "cut into small florets", 1),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("noodles", 200, "g", "cooked", 2),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 3, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 1),
      ]),
      slot("acid", "Acid", true, "rice-vinegar", [
        option("rice-vinegar", 1, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 1),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 20, "g", "finely grated"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 3, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
        option("scallion", 2, "piece", "sliced", 1),
      ]),
    ],
    steps: [
      {
        id: "marinate",
        template:
          "Toss {protein.name} with half the {sauce.name} and {acid.name} and set aside for 5 minutes.",
        translations: {
          tr: {
            template:
              "{protein.name} ile {sauce.name} yarısını ve {acid.name} karıştırın; 5 dakika bekletin.",
          },
        },
      },
      {
        id: "brown-protein",
        template:
          "Heat {oil.name} in a wok or wide pan over high heat. Fry {protein.name} for 4–5 minutes until golden on each side; remove.",
        translations: {
          tr: {
            template:
              "Wok veya geniş bir tavada {oil.name} yüksek ateşte ısıtın. {protein.name} her iki tarafı altın rengi olana kadar 4-5 dakika kızartın; çıkarın.",
          },
        },
      },
      {
        id: "cook-vegetable",
        template:
          "Add {garlic.name} and {ginger.name} to the same pan and stir for 30 seconds, then add {vegetable.name} and toss over high heat for 3–4 minutes until wilted but still bright.",
        translations: {
          tr: {
            template:
              "Aynı tavaya {garlic.name} ve {ginger.name} ekleyin, 30 saniye karıştırın; ardından {vegetable.name} ekleyin ve solmuş ama rengini korurken yüksek ateşte 3-4 dakika karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Return {protein.name} to the pan, pour over the remaining {sauce.name}, and toss to coat.",
        translations: {
          tr: {
            template:
              "{protein.name} tavaya geri ekleyin, kalan {sauce.name} dökün ve kaplayana kadar karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Serve over {base.name}, scattered with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin, kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "protein", "fat", "aroma", "starch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "snap-pea-edamame-stir-fry",
    nameTemplate: "{pea.name} & edamame stir-fry",
    translations: {
      tr: {
        nameTemplate: "{pea.name} & edamame kavurma",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("pea", "Pea", true, "snap-peas", [
        option("snap-peas", 300, "g", "strings removed", 3),
        option("snow-peas", 300, "g", "strings removed", 2),
      ]),
      slot("protein", "Protein", true, "edamame", [
        option("edamame", 200, "g", "shelled", 3),
        option("tofu", 250, "g", "pressed and cubed", 2),
        option("tempeh", 200, "g", "crumbled", 1),
      ]),
      slot("base", "Serving base", true, "rice", [
        option("rice", 300, "g", "cooked", 3),
        option("noodles", 200, "g", "cooked", 2),
        option("quinoa", 280, "g", "cooked", 1),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp", undefined, 3),
      ]),
      slot("acid", "Acid", true, "rice-vinegar", [
        option("rice-vinegar", 1, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 1),
      ]),
      slot("ginger", "Ginger", true, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
        option("scallion", 2, "piece", "sliced", 2),
        option("cashews", 2, "tbsp", "roughly chopped", 1),
      ]),
    ],
    steps: [
      {
        id: "mix-sauce",
        template:
          "Stir together {sauce.name}, {acid.name}, {ginger.name}, and {garlic.name} in a small bowl.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {sauce.name}, {acid.name}, {ginger.name} ve {garlic.name} karıştırın.",
          },
        },
      },
      {
        id: "stir-fry",
        template:
          "Heat {oil.name} in a wok or large pan over high heat. Add {pea.name} and {protein.name} and toss for 3–4 minutes until {pea.name} are bright green and just tender.",
        translations: {
          tr: {
            template:
              "Wok veya büyük bir tavada {oil.name} yüksek ateşte ısıtın. {pea.name} ve {protein.name} ekleyin; {pea.name} parlak yeşil renk alıp hafifçe yumuşayana kadar 3-4 dakika karıştırın.",
          },
        },
      },
      {
        id: "sauce",
        template:
          "Pour in the sauce and toss everything for 1 minute until coated.",
        translations: {
          tr: {
            template:
              "Sosu dökün ve her şey kaplanana kadar 1 dakika karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Serve over {base.name}, scattered with {finish.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine servis edin, kullanıyorsanız {finish.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["umami", "protein", "fresh", "crunch", "starch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "egg-fried-rice",
    nameTemplate: "Egg fried rice with {vegetable.name}",
    translations: {
      tr: {
        nameTemplate: "{vegetable.name} ile yumurtalı kızarmış pilav",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("rice", "Cooked rice", true, "rice", [
        option("rice", 300, "g", "day-old cooked"),
      ]),
      slot("egg", "Eggs", true, "egg", [
        option("egg", 2, "piece", undefined, 1, {
          safetyCue: "scramble quickly for 30 seconds until just set",
        }),
      ]),
      slot("vegetable", "Vegetable", true, "carrot", [
        option("carrot", 1, "piece", "finely diced", 3),
        option("corn", 80, "g", undefined, 2),
        option("snap-peas", 100, "g", "sliced", 1),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp"),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("ginger", "Ginger", false, "ginger", [
        option("ginger", 15, "g", "finely grated"),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("finish", "Finish", false, "scallion", [
        option("scallion", 2, "piece", "sliced", 3),
        option("sesame-seeds", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "scramble",
        template:
          "Heat {oil.name} in a large wok or frying pan over high heat. Beat {egg.name} with a pinch of {salt.name}, pour in and {egg.safetyCue}, then push to the side of the pan.",
        translations: {
          tr: {
            template:
              "Büyük bir wok veya tavada {oil.name} yüksek ateşte ısıtın. {egg.name} bir tutam {salt.name} ile çırpın, dökün ve {egg.safetyCue}, ardından tavanın kenarına itin.",
          },
        },
      },
      {
        id: "fry-aromatics",
        template:
          "Add a little more oil if needed. Fry {garlic.name} and {ginger.name} if using for 30 seconds, then add {vegetable.name} and stir-fry for 2 minutes.",
        translations: {
          tr: {
            template:
              "Gerekirse biraz daha yağ ekleyin. {garlic.name} ve kullanıyorsanız {ginger.name} 30 saniye kavurun, ardından {vegetable.name} ekleyin ve 2 dakika soteleyin.",
          },
        },
      },
      {
        id: "fry-rice",
        template:
          "Add {rice.name} and press it flat against the pan. Leave untouched for 1 minute to crisp, then stir everything together. Add {sauce.name} and toss to coat evenly.",
        translations: {
          tr: {
            template:
              "{rice.name} ekleyin ve tavaya düzce bastırın. Çıtırlaması için 1 dakika dokunmadan bekletin, ardından her şeyi karıştırın. {sauce.name} ekleyin ve eşit kaplanana kadar karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Season with {salt.name} and {black-pepper.name}, scatter {finish.name} if using, and serve straight from the pan.",
        translations: {
          tr: {
            template:
              "{salt.name} ve {black-pepper.name} ile tatlandırın, kullanıyorsanız {finish.name} serpin ve doğrudan tavadan servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "umami", "aroma", "fat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "vegetable-fritters",
    nameTemplate: "{vegetable.name} fritters with {dip.name}",
    translations: {
      tr: {
        nameTemplate: "{dip.name} ile {vegetable.name} mücveri",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 25,
    totalMinutes: 30,
    slots: [
      slot("vegetable", "Main vegetable", true, "zucchini", [
        option("zucchini", 2, "piece", "coarsely grated", 3),
        option("corn", 200, "g", undefined, 2),
        option("carrot", 2, "piece", "coarsely grated", 1),
      ]),
      slot("binder", "Flour binder", true, "chickpea-flour", [
        option("chickpea-flour", 80, "g"),
      ]),
      slot("egg", "Egg", false, "egg", [
        option("egg", 1, "piece", undefined, 1, {
          safetyCue: "this also fully cooks the egg binder",
        }),
      ]),
      slot("herb", "Herb", true, "parsley", [
        option("parsley", 15, "g", "finely chopped", 3),
        option("scallion", 2, "piece", "thinly sliced", 2),
        option("dill", 10, "g", "finely chopped", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 1, "piece", "finely grated"),
      ]),
      slot("spice", "Spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("dip", "Dipping sauce", false, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("sriracha", 1, "tbsp", undefined, 2),
        option("yogurt", 3, "tbsp", undefined, 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 3, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "prep",
        template:
          "If using grated {vegetable.name}, toss with a generous pinch of {salt.name}, set aside 5 minutes, then squeeze firmly in a clean cloth to remove excess moisture.",
        translations: {
          tr: {
            template:
              "Rendelenmiş {vegetable.name} kullanıyorsanız bol bir tutam {salt.name} ile karıştırın, 5 dakika bekletin; ardından fazla nemi gidermek için temiz bir bezde sıkıca sıkın.",
          },
        },
      },
      {
        id: "mix",
        template:
          "In a large bowl combine {vegetable.name}, {binder.name}, {egg.name} if using, {garlic.name}, {spice.name} if using, and {herb.name}. Season with {salt.name} and {black-pepper.name}. Mix to a thick batter.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {vegetable.name}, {binder.name}, kullanıyorsanız {egg.name}, {garlic.name}, kullanıyorsanız {spice.name} ve {herb.name} karıştırın. {salt.name} ve {black-pepper.name} ile tatlandırın. Koyu bir hamur elde edene kadar karıştırın.",
          },
        },
      },
      {
        id: "fry",
        template:
          "Heat {oil.name} in a large frying pan over medium heat. Drop heaped tablespoons of batter and flatten to about 1 cm thick. Cook 3 to 4 minutes per side until deep golden and crisp, working in batches to avoid crowding; if using egg, {egg.safetyCue}.",
        translations: {
          tr: {
            template:
              "Büyük bir tavada {oil.name} orta ateşte ısıtın. Kaşık kaşık hamuru dökün ve yaklaşık 1 cm kalınlığında düzleştirin. Sıkışmaması için parti parti, her iki tarafı koyu altın rengi ve çıtır olana kadar 3-4 dakika pişirin; yumurta kullanıyorsanız {egg.safetyCue}.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Drain briefly on paper towel and serve warm with {dip.name} if using alongside.",
        translations: {
          tr: {
            template:
              "Kağıt havlu üzerinde kısaca süzün ve kullanıyorsanız {dip.name} ile sıcak servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "fresh", "aroma"],
    editorialPriority: 7,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "stuffed-omelette",
    nameTemplate: "{filling.name} & {richness.name} stuffed omelette",
    translations: {
      tr: {
        nameTemplate: "{filling.name} & {richness.name} dolgulu omlet",
      },
    },
    foodType: "stir-fry",
    primaryCookingLocation: "stovetop",
    servings: 1,
    activeMinutes: 10,
    totalMinutes: 12,
    slots: [
      slot("egg", "Eggs", true, "egg", [
        option("egg", 3, "piece", undefined, 1, {
          safetyCue: "cook until just set but still slightly glossy on top",
        }),
      ]),
      slot("filling", "Filling", true, "mushroom", [
        option("mushroom", 100, "g", "thinly sliced", 3),
        option("spinach", 60, "g", "roughly chopped", 2),
        option("tomato", 1, "piece", "finely diced", 1),
      ]),
      slot("richness", "Creamy element", true, "feta", [
        option("feta", 40, "g", "crumbled", 3),
        option("yogurt", 1, "tbsp", undefined, 2),
      ]),
      slot("allium", "Allium", false, "scallion", [
        option("scallion", 1, "piece", "thinly sliced", 3),
        option("red-onion", 0.5, "piece", "finely diced", 2),
      ]),
      slot("herb", "Herb", false, "dill", [
        option("dill", 5, "g", "finely chopped", 3),
        option("parsley", 5, "g", "finely chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "prep-filling",
        template:
          "Heat half the {oil.name} in a non-stick pan over medium-high heat. Cook {filling.name} with a pinch of {salt.name} for 2–3 minutes until just tender. Add {allium.name} if using and cook 30 seconds more. Transfer to a plate.",
        translations: {
          tr: {
            template:
              "Yapışmaz bir tavada {oil.name} yarısını orta-yüksek ateşte ısıtın. {filling.name} bir tutam {salt.name} ile hafifçe yumuşayana kadar 2-3 dakika pişirin. Kullanıyorsanız {allium.name} ekleyin ve 30 saniye daha pişirin. Bir tabağa aktarın.",
          },
        },
      },
      {
        id: "beat-eggs",
        template:
          "Beat {egg.name} with {salt.name} and {black-pepper.name} until fully combined and slightly frothy.",
        translations: {
          tr: {
            template:
              "{egg.name} ile {salt.name} ve {black-pepper.name} tam birleşip hafif köpürene kadar çırpın.",
          },
        },
      },
      {
        id: "cook-omelette",
        template:
          "Wipe the pan, add remaining {oil.name} over medium heat. Pour in the egg mixture and swirl to coat the base. Cook undisturbed for 30 seconds, then gently push the set edges toward the centre, tilting the pan so the liquid egg runs to the sides; {egg.safetyCue}.",
        translations: {
          tr: {
            template:
              "Tavayı silin, kalan {oil.name} orta ateşte ekleyin. Yumurta karışımını dökün ve tabanı kaplaması için çevirin. 30 saniye dokunmadan pişirin; ardından pişmiş kenarları nazikçe ortaya doğru itin, sıvı yumurtanın kenarlara akması için tavayı eğin; {egg.safetyCue}.",
          },
        },
      },
      {
        id: "fill-fold",
        template:
          "Scatter {filling.name}, {richness.name}, and {herb.name} if using over one half of the omelette. Fold the other half over and slide onto a plate. Serve immediately.",
        translations: {
          tr: {
            template:
              "Omletin bir yarısına {filling.name}, {richness.name} ve kullanıyorsanız {herb.name} serpin. Diğer yarıyı üzerine katlayın ve bir tabağa kaydırın. Hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "fresh", "aroma", "salt"],
    editorialPriority: 7,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
];
